<script lang="ts">
export default {
  name: 'TextField',
};
</script>

<script setup lang="ts">
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    modelValue?: string | number;
    label?: string;
    placeholder?: string;
    disabled?: boolean;
    readonly?: boolean;
    error?: boolean;
    errorMessage?: string;
    hint?: string;
    type?: string;
    class?: string;
    prependIcon?: any;
    appendIcon?: any;
  }>(),
  {
    disabled: false,
    readonly: false,
    error: false,
    type: 'text',
  }
);

const emit = defineEmits<{
  'update:modelValue': [value: string | number];
}>();

const inputClasses = computed(() => {
  const classes = ['tw:input', 'tw:input-bordered', 'tw:w-full'];
  
  if (props.error) {
    classes.push('tw:input-error');
  }
  
  if (props.disabled) {
    classes.push('tw:input-disabled');
  }
  
  if (props.class) {
    classes.push(props.class);
  }
  
  return classes.join(' ');
});

const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  emit('update:modelValue', props.type === 'number' ? Number(target.value) || 0 : target.value);
};
</script>

<template>
  <div class="tw:form-control tw:w-full">
    <label v-if="label" class="tw:label">
      <span class="tw:label-text tw:text-sm">{{ label }}</span>
    </label>
    
    <div class="tw:relative tw:w-full">
      <div v-if="prependIcon" class="tw:absolute tw:left-3 tw:top-1/2 tw:-translate-y-1/2 tw:pointer-events-none">
        <component :is="prependIcon" class="tw:w-5 tw:h-5 tw:text-gray-400" />
      </div>
      
      <input
        :type="type"
        :class="[inputClasses, { 'tw:pl-10': prependIcon, 'tw:pr-10': appendIcon }]"
        :value="modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        @input="handleInput"
      />
      
      <div v-if="appendIcon" class="tw:absolute tw:right-3 tw:top-1/2 tw:-translate-y-1/2 tw:pointer-events-none">
        <component :is="appendIcon" class="tw:w-5 tw:h-5 tw:text-gray-400" />
      </div>
    </div>
    
    <label v-if="error && errorMessage" class="tw:label">
      <span class="tw:label-text-alt tw:text-error">{{ errorMessage }}</span>
    </label>
    
    <label v-else-if="hint" class="tw:label">
      <span class="tw:label-text-alt tw:text-gray-500">{{ hint }}</span>
    </label>
  </div>
</template>
